
export type Id = string;

export type ISODateTime = string;

export type ApiErrorCode =
  | 'auth_required'
  | 'forbidden'
  | 'not_found'
  | 'bad_request'
  | 'conflict'
  | 'payload_too_large'
  | 'rate_limited'
  | 'service_unavailable'
  | 'internal_error'
  | 'validation_error'
  | 'provider_rate_limited'
  | 'provider_not_configured'
  | 'provider_unavailable'
  | 'provider_failed'
  | (string & {});

export interface ValidationFieldError {
  loc: string;
  msg: string;
}

export interface ApiErrorBody {
  code: ApiErrorCode;
  message: string;
  error_id: string;
  details?: { fields?: ValidationFieldError[] } & Record<string, unknown>;
}

export interface ApiErrorEnvelope {
  error: ApiErrorBody;
}

export interface Page<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

export interface OkResponse {
  ok: boolean;
}

export type UserRole = 'admin' | 'editor' | 'viewer';

export const ROLE_RANK: Readonly<Record<UserRole, number>> = {
  viewer: 0,
  editor: 1,
  admin: 2,
};

export interface User {
  id: Id;
  username: string;
  display_name: string | null;
  role: UserRole;
  is_active: boolean;
  team: string | null;
  job: string | null;
  created_at: ISODateTime;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface SessionResponse {
  user: User;
  csrf_token: string;
  expires_at: ISODateTime;
  crm_link_warning: boolean;
}

export const SESSION_COOKIE = 'vp_session';
export const CSRF_COOKIE = 'vp_csrf';

export interface AdminUser {
  id: Id;
  username: string;
  role: UserRole;
  is_active: boolean;
  team: string | null;
  job: string | null;
  crm_matched: boolean;
  created_at: ISODateTime;
  last_login_at: ISODateTime | null;
}

export interface AdminUserCreateRequest {
  username: string;
  role: UserRole;
}

export interface AdminUserUpdateRequest {
  role?: UserRole | null;
  is_active?: boolean | null;
}

export interface AdminUserCredentials {
  user: AdminUser;
  password: string;
}

export type JobStatus =
  | 'draft'
  | 'queued'
  | 'running'
  | 'waiting_qc_tts'
  | 'waiting_qc_lipsync'
  | 'completed'
  | 'failed'
  | 'expired'
  | 'cancelled';

export const JOB_STATUSES: readonly JobStatus[] = [
  'draft',
  'queued',
  'running',
  'waiting_qc_tts',
  'waiting_qc_lipsync',
  'completed',
  'failed',
  'expired',
  'cancelled',
];

export const TERMINAL_JOB_STATUSES: readonly JobStatus[] = [
  'completed',
  'failed',
  'cancelled',
  'expired',
];

export type ArtifactKind = 'audio' | 'video' | 'image' | 'subtitle' | 'manifest' | 'other';

export interface Artifact {
  id: Id;
  job_id: Id;
  kind: ArtifactKind;
  content_type: string | null;
  size_bytes: number | null;
  created_at: ISODateTime;
}

export interface ArtifactUrlResponse {
  artifact_id: Id;
  url: string;
  expires_at: ISODateTime;
  content_type: string | null;
  size_bytes: number | null;
}

export type HealthComponentStatus = 'ok' | 'fail' | 'skipped';

export interface HealthComponent {
  status: HealthComponentStatus;
  latency_ms: number | null;
  error: string | null;
}

export interface HealthResponse {
  status: 'ok' | 'degraded';
  env: string;
  components: Record<string, HealthComponent>;
  providers_configured: Record<string, boolean>;
}

export interface MediaLink {
  url: string;
  expires_at: ISODateTime;
  content_type: string | null;
}

export type TaskStatus = 'pending' | 'running' | 'succeeded' | 'failed';

export const TASK_STATUSES: readonly TaskStatus[] = ['pending', 'running', 'succeeded', 'failed'];

export const TERMINAL_TASK_STATUSES: readonly TaskStatus[] = ['succeeded', 'failed'];

export type ToolTaskKind =
  | 'library.upload'
  | 'library.enrich'
  | 'voice.clone'
  | 'voice.import'
  | (string & {});

export interface ToolTask {
  id: Id;
  kind: ToolTaskKind;
  status: TaskStatus;
  created_at: ISODateTime;
  started_at: ISODateTime | null;
  finished_at: ISODateTime | null;
  progress: Record<string, unknown>;
  result: Record<string, unknown> | null;
  error: string | null;
  poll_after_seconds: number;
}

export type TtsModel = 'eleven_v3' | 'eleven_multilingual_v2' | 'sonic-3';

export type TtsProvider = 'elevenlabs' | 'cartesia';

export const TTS_MODEL_CARTESIA: TtsModel = 'sonic-3';

export const TTS_MODELS_BY_PROVIDER: Readonly<Record<TtsProvider, readonly TtsModel[]>> = {
  elevenlabs: ['eleven_multilingual_v2', 'eleven_v3'],
  cartesia: ['sonic-3'],
};

export const TTS_MODEL_EXPRESSIVE: TtsModel = 'eleven_v3';
export const TTS_MODEL_TUNABLE: TtsModel = 'eleven_multilingual_v2';

export const SPEED_MIN = 0.7;
export const SPEED_MAX = 1.2;

export interface TtsSynthesizeRequest {
  text?: string | null;
  segments?: string[] | null;
  voice_id?: string | null;
  voice_profile_id?: Id | null;
  character_id?: Id | null;
  language?: string;
  provider?: TtsProvider;
  model_id?: TtsModel | null;
  speed?: number;
  stability?: number;
  similarity_boost?: number;
  title?: string | null;
}

export interface WordTiming {
  index: number;
  word: string;
  start_ms: number;
  end_ms: number;
  interpolated: boolean;
}

export interface TtsSegment {
  index: number;
  duration_ms: number;
  audio: MediaLink;
  artifact_id: Id | null;
  words: WordTiming[];
  timing_source: string;
  word_map_tier: string;
  degraded: boolean;
  degradations: string[];
}

export interface Font {
  id: Id;
  name: string;
  filename: string;
  format: 'ttf' | 'otf';
  size_bytes: number;
  team: string | null;
  author_login: string | null;
  created_at: ISODateTime;
  can_edit: boolean;
}

export interface FontUploadResult {
  created: Font[];
  rejected: { filename: string; reason: string }[];
}

export interface FontUpdateRequest {
  name?: string;
  team?: string;
}

export interface DocxTextResult {
  text: string;
  chars: number;
}

export interface TtsToolResponse {
  run_id: Id;
  title: string;
  language: string;
  voice_id: string;
  voice_profile_id: Id | null;
  total_duration_ms: number;
  provider_calls: number;
  degraded: boolean;
  segments: TtsSegment[];
  created_at: ISODateTime | null;
}

export interface AuthorInfo {
  login: string;
  team: string | null;
  job: string | null;
}

export interface TtsRun {
  run_id: Id;
  title: string;
  language: string;
  status: JobStatus;
  voice_id: string;
  total_duration_ms: number;
  segment_count: number;
  created_at: ISODateTime;
  error_message: string | null;
  author: AuthorInfo | null;
}

export interface VoiceCatalogItem {
  voice_id: string;
  name: string;
  category: string;
  labels: Record<string, unknown>;
  preview_url: string;
  voice_profile_id: Id | null;
  character_id: Id | null;
}

export interface VoiceCatalogEntry {
  id: Id;
  provider: string;
  external_voice_id: string;
  name: string;
  category: string;
  language: string | null;
  accent: string | null;
  gender: string | null;
  age: string | null;
  use_case: string | null;
  description: string;
  preview_url: string | null;
  languages: string[];
  labels: Record<string, unknown>;
  is_owner: boolean;
  is_available: boolean;
  disappeared_at: ISODateTime | null;
  last_synced_at: ISODateTime | null;
  voice_profile_id: Id | null;
  character_id: Id | null;
}

export interface VoiceCatalogStats {
  provider: string;
  total: number;
  available: number;
  unavailable: number;
  by_category: Record<string, number>;
  last_synced_at: ISODateTime | null;
}

export interface VoiceCatalogSyncRequest {
  provider?: string;
  dry_run?: boolean;
  allow_empty?: boolean;
}

export interface SharedVoice {
  voice_id: string;
  public_owner_id: string;
  name: string;
  category: string;
  language: string | null;
  locale: string | null;
  accent: string | null;
  gender: string | null;
  age: string | null;
  use_case: string | null;
  description: string;
  preview_url: string | null;
  languages: string[];
  cloned_by_count: number;
  free_users_allowed: boolean;
  already_added: boolean;
  account_voice_id: string | null;
}

export interface SharedVoiceSearchResult {
  voices: SharedVoice[];
  has_more: boolean;
  total: number;
}

export interface SharedVoiceAddRequest {
  public_owner_id: string;
  voice_id: string;
  name: string;
}

export interface SharedVoiceAddResult {
  external_voice_id: string;
  name: string;
}

export interface VoiceLibraryParams {
  q?: string;
  language?: string;
  category?: string;
  gender?: string;
  age?: string;
  accent?: string;
  page?: number;
  page_size?: number;
}

export type InsertMediaType = 'video' | 'photo';

export type InsertMediaTypeFilter = 'any' | InsertMediaType;

export type TagsMode = 'all' | 'any';

export type InsertSortKey =
  | 'relevance'
  | 'created_at'
  | '-created_at'
  | 'duration'
  | '-duration'
  | 'title';

export const INSERT_SORT_KEYS: readonly InsertSortKey[] = [
  'relevance',
  'created_at',
  '-created_at',
  'duration',
  '-duration',
  'title',
];

export type SearchBranch = 'lexical' | 'vector' | 'external';

export interface Insert {
  id: Id;
  title: string;
  description: string | null;
  source: string;
  source_url: string | null;
  media_type: InsertMediaType;
  duration_ms: number | null;
  width: number | null;
  height: number | null;
  fps: number | null;
  tags: string[];
  mood: string[];
  geo: string[];
  symbolic_tags: string[];
  tagged: boolean;
  embedded: boolean;
  created_at: ISODateTime;
  created_by: Id | null;
  media: MediaLink | null;
  thumbnail: MediaLink | null;
  score: number | null;
  branches: SearchBranch[];
}

export interface InsertSearchRequest {
  q: string;
  language?: string | null;
  tags?: string[];
  tags_mode?: TagsMode;
  mood?: string[];
  geo?: string[];
  source?: string[];
  media_type?: InsertMediaTypeFilter;
  duration_min_ms?: number | null;
  duration_max_ms?: number | null;
  min_width?: number | null;
  min_height?: number | null;
  has_tags?: boolean | null;
  has_embedding?: boolean | null;
  created_from?: ISODateTime | null;
  created_to?: ISODateTime | null;
  limit?: number;
  offset?: number;
}

export interface InsertSearchResponse {
  items: Insert[];
  total: number;
  limit: number;
  offset: number;
  degraded: boolean;
  degraded_reason: string;
  language: string;
  fts_config: string;
  lexical_hits: number;
  vector_hits: number;
  filters_applied_after_ranking: boolean;
}

export interface FacetValue {
  value: string;
  count: number;
}

export interface LibraryFacets {
  sources: FacetValue[];
  tags: FacetValue[];
  mood: FacetValue[];
  geo: FacetValue[];
  total: number;
  tagged: number;
  embedded: number;
  videos: number;
  photos: number;
}

export interface EnrichRequest {
  item_ids?: Id[];
  limit?: number | null;
  dry_run?: boolean;
  force?: boolean;
  frames?: number;
}

export interface UploadAccepted {
  task: ToolTask;
  accepted_files: number;
}

export interface InsertUploadRequest {
  files: File[];
  tags?: string;
  mood?: string;
  geo?: string;
  source?: string;
}

export const MAX_UPLOAD_FILES = 1000;
export const MAX_UPLOAD_BYTES = 512 * 1024 * 1024;

/** Ключ провайдера. Само значение наружу не отдаётся никогда, только маска. */
export interface SecretItem {
  key: string;
  label: string;
  is_set: boolean;
  masked: string;
  from_env: boolean;
}

/** Пустое поле означает «не менять», а не «стереть». */
export interface SecretsUpdateRequest {
  elevenlabs_api_key?: string | null;
  cartesia_api_key?: string | null;
  llm_api_key?: string | null;
  dreamface_api_key?: string | null;
  pexels_api_key?: string | null;
  pixabay_api_key?: string | null;
  openrouter_api_key?: string | null;
}

export interface SecretsSaved {
  saved: string[];
  items: SecretItem[];
}

export type VoiceStatus = 'pending' | 'ready' | 'failed' | 'evicted';

export const VOICE_STATUSES: readonly VoiceStatus[] = ['pending', 'ready', 'failed', 'evicted'];

export type CharacterCategory = 'doctors' | 'celebrities' | 'hosts' | 'testimonials';

export const ALWAYS_SHARED_CATEGORIES: readonly CharacterCategory[] = [
  'celebrities',
  'testimonials',
];

export const CHARACTER_CATEGORIES: readonly CharacterCategory[] = [
  'doctors',
  'celebrities',
  'hosts',
  'testimonials',
];

export const CHARACTER_CATEGORY_LABELS: Readonly<Record<CharacterCategory, string>> = {
  doctors: 'Врачи',
  celebrities: 'Селебы',
  hosts: 'Ведущие',
  testimonials: 'Отзывы',
};

export interface VoiceSummary {
  voice_profile_id: Id;
  provider: string;
  status: VoiceStatus;
  is_current: boolean;
  external_voice_id: string | null;
  name: string;
  created_by: Id | null;
  author_login: string | null;
  team: string | null;
}

export interface Character {
  id: Id;
  name: string;
  language: string;
  category: CharacterCategory | null;
  is_active: boolean;
  created_by: Id | null;
  author_login: string | null;
  team: string | null;
  has_avatar: boolean;
  current_voice: VoiceSummary | null;
  voices: VoiceSummary[];
  ready: boolean;
  missing: string[];
  created_at: ISODateTime;
  updated_at: ISODateTime | null;
}

export interface CharacterCreateRequest {
  name: string;
  language?: string;
  category?: CharacterCategory | null;
}

export interface CharacterUpdateRequest {
  name?: string | null;
  language?: string | null;
  category?: CharacterCategory | null;
  is_active?: boolean | null;
  team?: string | null;
}

export interface CharacterJob {
  job_id: Id;
  title: string;
  status: JobStatus;
  language: string;
  created_at: ISODateTime;
}

export interface CharacterAvatarUploadRequest {
  file: File;
}

export type TranslateSourceKind = 'media' | 'srt';

export interface TranslateCue {
  start_ms: number;
  end_ms: number;
  source: string;
  text: string;
}

export interface TranslateRun {
  id: Id;
  title: string;
  status: TaskStatus;
  target_language: string;
  source_kind: TranslateSourceKind;
  two_words: boolean;
  task_id: Id | null;
  cues_total: number;
  error: string | null;
  preview: TranslateCue[];
  srt: MediaLink | null;
  author: AuthorInfo | null;
  created_at: ISODateTime;
}

export interface TranslateRunRequest {
  file: File;
  target_language?: string;
  two_words?: boolean;
}

export type SubtitleEffect = 'none' | 'pop' | 'fade' | 'karaoke' | 'line_accent' | 'word';

export interface SubtitlePreset {
  key: string;
  label: string;
  hint: string;
  font_family: string;
  font_file: string;
  cyrillic: boolean;
  size_pct: number;
  primary: string;
  outline: string;
  outline_w: number;
  shadow: number;
  back: string;
  back_alpha: number;
  border_style: number;
  uppercase: boolean;
  align: number;
  margin_pct: number;
  effect: SubtitleEffect | (string & {});
  accent: string;
  accent_cycle: string[];
  accent_mode: string;
}

export interface SubtitleSettings {
  retention_days: number;
}

export type VideoGenTab = 'anchor' | 'live_photo';

export type VideoGenStage = 'submitted' | 'pending' | 'in_progress' | 'done';

export type VideoGenStatus = 'running' | 'done' | 'failed';

export interface VideoGenServiceStatus {
  configured: boolean;
  /** null when OpenRouter did not answer the balance check. */
  balance_ok: boolean | null;
  retention_days: number;
  /** Starting text of each tab's prompt field. */
  prompts: Record<VideoGenTab, string>;
}

export interface VideoGeneration {
  id: Id;
  tab: VideoGenTab;
  tab_label: string;
  status: VideoGenStatus;
  stage: VideoGenStage | null;
  error: string | null;
  error_code: string | null;
  cost_usd: number | null;
  duration_s: number | null;
  aspect_ratio: string;
  prompt: string;
  has_end_frame: boolean;
  video: MediaLink | null;
  download: MediaLink | null;
  files_expired: boolean;
  task_id: Id | null;
  author: AuthorInfo | null;
  created_at: ISODateTime;
  finished_at: ISODateTime | null;
}

export interface VideoGenerationPage extends Page<VideoGeneration> {
  /** Sum over every generation the admin sees; null for non-admins. */
  total_cost_usd: number | null;
}

export interface SubtitlePos {
  x: number;
  y: number;
}

export interface SubtitleItem {
  filename: string;
  status: 'pending' | 'done' | 'failed';
  error: string | null;
  pos: SubtitlePos | null;
  size: number;
  bytes: number;
  source_alive: boolean;
  frame: MediaLink | null;
  output: MediaLink | null;
  output_name: string | null;
  output_deleted: boolean;
}

export type SubtitleBatchStatus = 'pending' | 'transcribing' | 'rendering' | 'done' | 'failed';

export interface SubtitleBatch {
  id: Id;
  title: string;
  status: SubtitleBatchStatus;
  preset: string;
  shared_track: boolean;
  total: number;
  done: number;
  failed: number;
  language: string | null;
  transcript: string | null;
  items: SubtitleItem[];
  error: string | null;
  geo: string | null;
  expired: boolean;
  task_id: Id | null;
  author: AuthorInfo | null;
  created_at: ISODateTime;
}

export interface SubtitleBatchRequest {
  files: File[];
  preset: string;
  shared_track: boolean;
  size: number;
}

export interface SubtitleLayoutRequest {
  x: number;
  y: number;
  size: number;
}

export const PROMPT_LABEL_MAX = 200;
export const PROMPT_BODY_MAX = 20000;

export interface Prompt {
  id: Id;
  label: string;
  body: string;
  created_by: Id | null;
  author_login: string | null;
  team: string | null;
  created_at: ISODateTime;
  updated_at: ISODateTime | null;
}

export interface PromptCreateRequest {
  label: string;
  body?: string;
}

export interface PromptUpdateRequest {
  label?: string | null;
  body?: string | null;
  team?: string | null;
}

export interface Voice {
  id: Id;
  character_id: Id | null;
  character_name: string;
  language: string;
  name: string;
  provider: string;
  status: VoiceStatus;
  is_current: boolean;
  external_voice_id: string | null;
  usable: boolean;
  created_by: Id | null;
  author_login: string | null;
  team: string | null;
  created_at: ISODateTime;
  updated_at: ISODateTime | null;
}

export interface VoiceCreateRequest {
  character_id?: Id;
  language?: string;
  name?: string;
  provider?: string;
  external_voice_id?: string;
  make_current?: boolean;
  sample: File;
}

export interface VoiceUpdateRequest {
  name?: string | null;
  is_current?: boolean | null;
  external_voice_id?: string | null;
  team?: string | null;
}

export interface VoiceImportRequest {
  source?: string;
  path: string;
  language?: string;
  provider?: string;
  dry_run?: boolean;
  overwrite?: boolean;
  limit?: number | null;
  parse_legacy_names?: boolean;
}

export interface VoiceImportSpec {
  sources: string[];
  manifest_columns: Record<string, string[]>;
  audio_suffixes: string[];
  root_env: string;
  root_configured: boolean;
  open_questions: string[];
}

export function isTerminal(status: JobStatus): boolean {
  return TERMINAL_JOB_STATUSES.includes(status);
}

export function hasRole(user: User | undefined, min: UserRole): boolean {
  if (!user) return false;
  return ROLE_RANK[user.role] >= ROLE_RANK[min];
}

export function isTaskFinished(status: TaskStatus | undefined): boolean {
  if (!status) return false;
  return TERMINAL_TASK_STATUSES.includes(status);
}

export function isVoiceUsable(voice: Voice | undefined | null): boolean {
  return Boolean(voice?.usable);
}

export function insertDurationSeconds(item: Insert): number | null {
  if (!item.duration_ms) return null;
  return item.duration_ms / 1000;
}

export interface PlacementDensity {
  profile?: string;
  min_insert_ms?: number;
  max_insert_ms?: number;
  min_visible_ms?: number;
  min_gap_ms?: number;
  hook_guard_ms?: number;
  tail_guard_ms?: number;
  allow_adjacent?: boolean;
  density_budget?: number;
  inserts_per_minute?: number;
  inserts_per_100_words?: number;
  words_per_second?: number;
}

export interface PlacementBaseTrackItem {
  segment_index: number;
  clip_key: string;
  duration_ms: number;
}

export interface PlacementProposalRequest {
  text?: string;
  segments?: string[];
  context?: string;
  language?: string;
  density?: PlacementDensity;
  word_maps?: Record<string, unknown>[];
  anchors?: Record<string, unknown>[];
  alternatives?: number;
  allow_media_reuse?: boolean;
  base_track?: PlacementBaseTrackItem[];
  fps?: string;
  width?: number;
  height?: number;
}

export interface AudioTimingsResult {
  word_maps: Record<string, unknown>[];
  text: string;
  origin: 'forced_alignment' | 'stt';
  timing_source: string;
  audio_duration_ms: number | null;
  alignment_loss: number | null;
  word_count: number;
  warnings: unknown[];
}

export interface PlacementVideoSource {
  source_id: Id;
  filename: string;
  duration_ms: number;
  width: number;
  height: number;
  fps: number;
  size_bytes: number;
  has_audio: boolean;
  created_at: ISODateTime;
  reused: boolean;
}

export type PlacementTimingsMode = 'align' | 'stt';

export type PlacementTimingsOrigin = 'forced_alignment' | 'stt';

export interface PlacementVideoRunRequest {
  source_id: Id;
  script?: string;
  language?: string;
  mode?: PlacementTimingsMode;
  density?: PlacementDensity;
  alternatives?: number;
  allow_media_reuse?: boolean;
  subtitles?: boolean;
  fps?: string | null;
  force?: boolean;
}

export interface PlacementTimingWarning {
  code: string;
  message: string;
  token_index: number | null;
}

export interface PlacementVideoTimings {
  provider: string;
  origin: PlacementTimingsOrigin;
  timing_source: string;
  note: string;
  words: number;
  alignment_loss: number | null;
  language_code: string | null;
  language_probability: number | null;
  audio_duration_ms: number | null;
  text: string;
  warnings: PlacementTimingWarning[];
}

export interface PlacementVideoRun {
  run_id: Id;
  status: TaskStatus;
  stage: string;
  progress: number;
  source: PlacementVideoSource;
  timings: PlacementVideoTimings | null;
  proposal_id: Id | null;
  proposal: Record<string, unknown> | null;
  video: MediaLink | null;
  fcpxml: MediaLink | null;
  inserts: number;
  subtitles_burned: boolean;
  render: Record<string, unknown> | null;
  timeline: Record<string, unknown>[];
  excluded: Record<string, unknown>[];
  dropped: Record<string, unknown>[];
  warnings: string[];
  error: string | null;
  task_id: Id | null;
  created_at: ISODateTime;
  finished_at: ISODateTime | null;
}

export const MAX_PLACEMENT_VIDEO_BYTES = 4 * 1024 * 1024 * 1024;

export type CutRejectReason =
  | 'none'
  | 'no_face'
  | 'multi_face'
  | 'small_face'
  | 'scene_cut'
  | 'bbox_jump'
  | 'identity'
  | 'pose'
  | 'blur'
  | 'exposure'
  | 'mouth_occluded';

export interface CutSettings {
  max_yaw_deg: number;
  ideal_yaw_deg: number;
  max_pitch_deg: number;
  ideal_pitch_deg: number;
  min_face_area_frac: number;
  min_duration_s: number;
}

export const DEFAULT_CUT_SETTINGS: CutSettings = {
  max_yaw_deg: 35,
  ideal_yaw_deg: 10,
  max_pitch_deg: 25,
  ideal_pitch_deg: 12,
  min_face_area_frac: 0.02,
  min_duration_s: 15,
};

export interface CutSource {
  source_id: Id;
  filename: string;
  duration_s: number;
  width: number;
  height: number;
  fps: number;
  size_bytes: number;
  created_at: ISODateTime;
  reused: boolean;
}

export interface CutSourceUploadRequest {
  file: File;
}

export interface CutMetrics {
  mean_yaw: number;
  mean_pitch: number;
  mean_roll: number;
  yaw_spread: number;
  mean_mouth_score: number;
  mean_sharpness: number;
  mean_face_area: number;
  frames: number;
}

export interface CutBreakdown {
  frame_score: number;
  duration_factor: number;
  stability_factor: number;
}

export interface CutCheck {
  key: 'pose' | 'mouth' | 'sharpness' | 'size';
  label: string;
  ok: boolean;
}

export interface CutCandidate {
  index: number;
  start_s: number;
  end_s: number;
  duration_s: number;
  score: number;
  verdict: string;
  metrics: CutMetrics;
  breakdown: CutBreakdown;
  checks: CutCheck[];
  thumbnail: MediaLink | null;
  clip: MediaLink | null;
  audio: MediaLink | null;
  extracted: boolean;
}

export interface CutRejection {
  reason: string;
  label: string;
  frames: number;
  share: number;
}

export interface CutEmptyReason {
  headline: string;
  advice: string;
  reasons: CutRejection[];
}

export interface CutRun {
  run_id: Id;
  status: TaskStatus;
  settings: CutSettings;
  source: CutSource;
  progress: number;
  stage: string;
  analyzed_frames: number;
  sample_period_s: number;
  rejections: Record<string, number>;
  candidates: CutCandidate[];
  empty_reason: CutEmptyReason | null;
  error: string | null;
  task_id: Id | null;
  created_at: ISODateTime;
  finished_at: ISODateTime | null;
}

export interface CutRunRequest {
  source_id: Id;
  settings?: CutSettings;
  force?: boolean;
}

export interface CutExtractRequest {
  index: number;
  with_audio?: boolean;
}

export interface CutExtractResult {
  run_id: Id;
  index: number;
  reused: boolean;
  clip: MediaLink | null;
  audio: MediaLink | null;
}

export const MAX_CUT_SOURCE_BYTES = 4 * 1024 * 1024 * 1024;

export const CUT_SOURCE_ACCEPT = 'video/*,.mp4,.mov,.m4v,.mkv,.webm,.avi';

export interface ActivityItem {
  id: number;
  created_at: ISODateTime;
  login: string | null;
  team: string | null;
  job: string | null;
  action: string;
  action_label: string;
  entity_type: string;
  entity_label: string;
  entity_id: string | null;
  subject: string;
}

export interface ActionOption {
  action: string;
  label: string;
}

export interface ActivityFilterOptions {
  teams: string[];
  users: string[];
  actions: ActionOption[];
}
